import React, { forwardRef, useState, useEffect, useImperativeHandle } from "react";
import PropTypes from "prop-types";

import FormGroup from "shared-anpost-components/components/form/form_group/FormGroup";
import InputGroup from "shared-anpost-components/components/form/input_group/InputGroup";
import Input from "shared-anpost-components/components/form/input/Input";
import FieldError from "shared-anpost-components/components/form/fieldError/FieldError";
import Label from "shared-anpost-components/components/form/label/Label";

import { VerifyPhoneNumberStyled } from "./VerifyPhoneNumberStyled";
import Icon from "../../../_common/icons/ReturnIcons";
import Button from "../../../_common/utils/Button";
import { virtualPageView } from "../../../../helpers/Helpers";

/**
 * @param {{
 *  number: string,
 *  submitHandler: (code: string) => void,
 *  hasError: boolean,
 *  singleItem: boolean,
 * }} props
 */

const VerifyPhoneNumberStageVerification = forwardRef(({ number, submitHandler, hasError, singleItem }, ref) => {
    const [code, setCode] = useState("");
    const [codeError, setCodeError] = useState("");
    const [focused, setFocused] = useState([]);

    useImperativeHandle(ref, () => ({
        triggerAction() {
            setCode("");
            setCodeError("");
            setFocused([]);
        }
    }));

    useEffect(() => {
        let pageViewUrl = window.location.pathname?.replace(/\/+$/, ""); // remove trailing slash
        if (singleItem) {
            pageViewUrl = `${pageViewUrl}/item`;
        }
        virtualPageView(`${pageViewUrl}/Add-your-mobile-number/Code-sent`, `${document.title} - Add your mobile phone number - Code Sent`);
    }, []);

    const ensureLeadingZero = () => {
        if (number[0] !== "0") {
            return `0${number}`;
        }
        return number;
    };

    const codeVerificationBlurHandler = () => {
        if (code === "") {
            const newVal = focused.filter(item => item !== "code");
            setFocused(newVal);
            setCodeError("Field can not be left empty");
        } else {
            setCodeError("");
        }
    };

    const handleFocus = event => {
        const { name, value } = event.target;
        const newVal = value && code ? focused.filter(item => item !== name) : [...focused, name];
        setFocused(newVal);
    };

    const verifyAndSubmit = event => {
        event.preventDefault();

        codeVerificationBlurHandler();
        if (code !== "" && codeError === "") {
            setCodeError("");
            submitHandler(code);
        }
    };

    useEffect(() => {
        if (hasError) {
            setCodeError("Access code invalid or expired");
        }
    }, [hasError]);

    return (
        <VerifyPhoneNumberStyled>
            <>
                <div className="titleWrapper">
                    <Icon src="mobileVerificationIcon" />
                    <div className="headerTitle">Verify your mobile number</div>
                    {!hasError ? (
                        <span className="titleInfo">
                            Please enter the verification code we sent by SMS to <b>{ensureLeadingZero()}</b>.{" "}
                        </span>
                    ) : (
                        ""
                    )}
                </div>
                <>
                    <div>
                        <form onSubmit={verifyAndSubmit}>
                            <div className="inputWrapper">
                                <FormGroup error={codeError !== ""} focused={focused.includes("code") || code}>
                                    <InputGroup>
                                        <Label htmlFor="code" text="Enter code" />
                                        <Input
                                            id="code"
                                            type="tel"
                                            name="code"
                                            focus={handleFocus}
                                            blur={codeVerificationBlurHandler}
                                            value={code}
                                            change={event => setCode(event.target.value)}
                                            maxLength="6"
                                        />
                                    </InputGroup>
                                    {!codeError && <p className="inputInfo">Your code will expire in 30 minutes</p>}
                                    <div className="errorWrapper">{codeError !== "" && <FieldError error={codeError} />}</div>
                                </FormGroup>
                            </div>

                            <div className="submitPanel">
                                <Button type="submit" className="gtm-cta">
                                    Verify
                                </Button>
                            </div>
                        </form>
                    </div>
                </>
            </>
        </VerifyPhoneNumberStyled>
    );
});

export default VerifyPhoneNumberStageVerification;

VerifyPhoneNumberStageVerification.displayName = "VerifyPhoneNumberStageVerification";
VerifyPhoneNumberStageVerification.propTypes = {
    number: PropTypes.string.isRequired,
    submitHandler: PropTypes.func.isRequired,
    hasError: PropTypes.bool.isRequired,
    singleItem: PropTypes.bool
};
